import bbox from '@turf/bbox';
import { useMapLocation } from 'kineticUtils/hooks';
import 'leaflet-contextmenu';

import React, { PropsWithChildren, memo, useEffect, useMemo } from 'react';

import { BBox2d } from '@turf/helpers/dist/js/lib/geojson';

import { LngLatBoundsLike } from 'maplibre-gl';
import AutoSizer from 'react-virtualized-auto-sizer';
import { MapContext } from '../MapContext';
import { useMapContextState } from '../MapContext/useMapContextState';
import { MapMarkerContext, MapMarkerContextFunctions } from '../MapMarkerContext';
import { useMapMarkerContext } from '../MapMarkerContext/useMapMarkerContext';
import DeckGlMapContainer from './DeckGlMapContainer/DeckGlMapContainer';
import { BaseMapProps } from './types';
import MapSession from './MapSession';

const BaseMap: React.FunctionComponent<PropsWithChildren<BaseMapProps>> = ({
  entityDrawer,
  geometry,
  layers,
  children,
  viewState,
  trackMapSession,
  ...restProps
}) => {
  const { bounds, viewState: viewStatePreference, layers: layersPreference } = useMapLocation();
  const { contextValue: mapContextValue, extraDeckGlLayers } = useMapContextState(
    !!entityDrawer,
    layers ?? layersPreference
  );
  const { clusteringEnabled, selectedLayers, setSelectedLayers } = mapContextValue;

  const { functionsContext, dataContext } = useMapMarkerContext(selectedLayers);
  const mapBounds: undefined | LngLatBoundsLike = useMemo(() => {
    if (geometry) {
      const boundBox = bbox(geometry) as BBox2d;
      return [
        [boundBox[0], boundBox[1]],
        [boundBox[2], boundBox[3]],
      ];
    }
    return bounds;
  }, [bounds, geometry]);

  useEffect(() => {
    if (!layersPreference.length || layers) {
      return;
    }
    setSelectedLayers(layersPreference);
    // do this only on layersPreference change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layersPreference]);

  return (
    <MapContext.Provider value={mapContextValue}>
      <MapMarkerContextFunctions.Provider value={functionsContext}>
        <MapMarkerContext.Provider value={dataContext}>
          <AutoSizer
            style={{
              height: '100%',
              width: '100%',
              overflow: 'hidden',
              position: 'relative',
              zIndex: 0,
            }}
          >
            {({ height, width }) => {
              return (
                <DeckGlMapContainer
                  addToCache={functionsContext.addToCache}
                  extraLayers={extraDeckGlLayers}
                  viewState={!geometry ? viewState ?? viewStatePreference : undefined}
                  height={height}
                  width={width}
                  cluster={clusteringEnabled}
                  bounds={mapBounds!}
                  entityDrawer={entityDrawer}
                  {...restProps}
                >
                  {children}
                  {trackMapSession && <MapSession />}
                </DeckGlMapContainer>
              );
            }}
          </AutoSizer>
        </MapMarkerContext.Provider>
      </MapMarkerContextFunctions.Provider>
    </MapContext.Provider>
  );
};

export default memo(BaseMap);
